import React, { useEffect, useState } from "react";
import { gaEvent } from "../lib/ga";
import { Helmet } from "react-helmet-async";
import SiteLayout from "./SiteLayout";
import { Testimonials, useReviewsLabel } from "./Testimonials";
import { CalendlyLink, SmartLink } from "./SiteHeader";
import { Intro, FinalCta, FaqSection } from "./sections";
import { publicApi, fmtRate, minutesAgo } from "../pages/public/api";
import { SlotRate } from "./SlotRate";
import HUB from "./content/ratesHub.json";

// Shared copy source (content/ratesHub.json) — the backend prerender renders the same file, so static HTML and React never drift.
export const fillPlace = (s, place) => (s || "").split("{place}").join(place || "Alberta");

// Brand-new Hello Mortgage rates page. Data + funnel drawer + SEO tags live in Widget.jsx; this is the whole visual layer.
export const money = (n) => (n == null ? "—" : `$${Math.round(n).toLocaleString("en-CA")}`);
export const payment = (rate, principal = 500000, years = 25) => {
  if (rate == null) return null;
  const r = Math.pow(1 + rate / 200, 1 / 6) - 1;
  const n = years * 12;
  return (principal * r) / (1 - Math.pow(1 + r, -n));
};
const RISK = { stable: ["Steady", "risk-stable"], volatile: ["Jumpy", "risk-volatile"], up: ["Climbing", "risk-up"], down: ["Easing", "risk-stable"] };
const card = (rates, term) => rates?.cards?.find((c) => c.term === term);

export function RatesShell({ children, loading = false }) {
  return (
    <SiteLayout bodyClass="rates-site" loading={loading}>
      <Helmet><link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Serif+Display:ital@0;1&display=swap" rel="stylesheet" /></Helmet>
      {children}
    </SiteLayout>
  );
}

export function RatesPage({ cityLabel, headlineRate, rates, ratesLoading, signal, desk, openDrawer, onCompareAll }) {
  const [insight, setInsight] = useState(null);
  const [outlook, setOutlook] = useState(null);
  const [reading, setReading] = useState(null);
  const [seo, setSeo] = useState(null);
  useEffect(() => {
    publicApi.get("/insights/general").then((r) => setInsight(r.data)).catch(() => {});
    publicApi.get("/fixed-outlook").then((r) => setOutlook(r.data)).catch(() => {});
    publicApi.get("/rates-reading").then((r) => setReading(r.data)).catch(() => {});
    publicApi.get("/rates-seo").then((r) => setSeo(r.data)).catch(() => {});
  }, []);
  const place = cityLabel || "Alberta";
  const boc = desk?.boc;
  const full = (preset) => openDrawer({ mode: "full", ...(preset || {}) });
  // Deep link: /rates/?intent=switch_to_fixed opens the "Run my numbers" form directly.
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.get("intent") === "switch_to_fixed") { full({ presetIntent: "switch_to_fixed" }); gaEvent("SwitchFormOpen", { content_name: "switch_to_fixed" }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <>
      <RatesHero place={place} headlineRate={headlineRate} rates={rates} ratesLoading={ratesLoading} boc={boc} />
      <IntentBand onIntent={full} />
      <Shortlist rates={rates} ratesLoading={ratesLoading} openDrawer={openDrawer} onCompareAll={onCompareAll} />

      {/* 3 · HOW WE STACK UP */}
      <RatesTrust onCta={() => full()} />

      {/* 4 · RATE FORECAST */}
      <section className="rt-read" id="rate-intelligence" data-testid="rate-intelligence">
        <div className="wrap">
          <div className="rt-read-head">
            <Intro eyebrow={HUB.forecast.eyebrow} heading={<>{fillPlace(HUB.forecast.heading, place)} <em>{HUB.forecast.heading_em}</em></>} text={HUB.forecast.text} />
            <img className="rt-read-art" src="/brand/rates-crystal-ball.png" alt="A crystal ball with percent signs floating inside" data-testid="forecast-art" />
          </div>
          <div className="rt-forecasts">
            <FixedPanel insight={insight} signal={signal} outlook={outlook} onHold={() => full({ entryCta: "fixed_nudge" })} />
            <VariablePanel insight={insight} boc={boc} prime={rates?.prime_rate} />
          </div>
          <RateHistoryChart />
          <RateIntel signal={signal} insight={insight} weekly={reading?.weekly} />
        </div>
      </section>

      {/* 5 · ALBERTA HOUSING MARKET */}
      <AlbertaMarket rows={seo?.market} place={place} />

      {/* 6 · FIXED VS VARIABLE EXPLAINER */}
      <FixedVsVariable onCta={() => full()} />

      {/* 7 · DECISION DESK */}
      <section className="rt-desk" id="decision-desk" data-testid="decision-desk">
        <div className="wrap">
          <Intro eyebrow={HUB.desk.eyebrow} heading={<>{HUB.desk.heading} <em>{HUB.desk.heading_em}</em></>} text={HUB.desk.text} />
          <DecisionDesk desk={desk} onCta={() => full({ entryCta: "decision_desk" })} />
          <div className="rt-switch">
            <div><span className="eyebrow">{HUB.desk.switch_eyebrow}</span><h3>{HUB.desk.switch_heading}</h3><p>{HUB.desk.switch_text}</p></div>
            <button type="button" className="btn light" onClick={() => { gaEvent("SwitchFormOpen", { content_name: "switch_to_fixed" }); full({ presetIntent: "switch_to_fixed" }); }} data-testid="switch-cta">{HUB.desk.switch_cta}</button>
          </div>
        </div>
      </section>

      <RateFactors onCta={() => full({ entryCta: "rate_factors" })} />
      <RatesSteps onCta={() => full({ entryCta: "how_it_works" })} />
      <GoDeeper reading={reading} />
      <RatesFaq seo={seo} place={place} />
      <Testimonials topics={["rates", "renewal", "pre-approval"]} />
      <FinalCta />
      <StickyHoldBar outlook={outlook} />
      <TextMyRates />
    </>
  );
}

// 1 · HERO — shared by /rates and the city rates pages.
export function RatesHero({ place, headlineRate, rates, ratesLoading, boc, compareHref = "/compare-all", eyebrow, intro }) {
  const fixed5 = card(rates, "5yr fixed"), var5 = card(rates, "5yr variable"), fixed3 = card(rates, "3yr fixed");
  return (
    <section className="hero rt-hero" data-testid="hero-section">
      <div className="wrap rt-hero-grid">
        <div className="rt-hero-left">
          <div className="eyebrow" data-testid="rates-hero-eyebrow">{eyebrow || "Today's best rates · live from lender desks"}</div>
          <h1 className="rt-h1">{place} mortgage rates, today<span className="hero-dot">.</span></h1>
          <div className="rt-giant" data-testid="hero-headline-rate" aria-live="polite">
            <SlotRate rate={headlineRate?.rate} loading={ratesLoading} star />
          </div>
          <div className="rt-giant-label">{headlineRate?.label || "5-Year Variable"} <span>· lowest live lender rate today</span></div>
          <div className="rt-fresh" data-testid="hero-headline-meta"><span className="live-dot" />{rates?.updated_at_iso ? `Updated ${minutesAgo(rates.updated_at_iso)}` : ratesLoading ? "Checking today's lender rate sheets…" : "Updated every business day"} · straight from lender rate desks, not last week's flyer</div>
        </div>
        <div className="rt-flock" aria-hidden="true">
          {[["a", 1], ["b", .5], ["c", .36]].map(([k, sc]) => <WingedPercent key={k} variant={k} scale={sc} />)}
        </div>
      </div>
      <div className="wrap rt-ticker" data-testid="rates-ticker">
        {[["5-yr fixed", fixed5?.rate], ["5-yr variable", var5?.rate], ["3-yr fixed", fixed3?.rate]].map(([l, r]) => <a href="#shortlist" key={l}><b><SlotRate rate={r} loading={ratesLoading} /></b>{l}</a>)}
        {rates?.prime_rate != null && <a href="#decision-desk"><b>{fmtRate(rates.prime_rate)}</b>Prime rate</a>}
        {boc?.next && <a href="#decision-desk"><b>{boc.next.days_away}</b>days to next BoC rate announcement</a>}
        <SmartLink href={compareHref} className="rt-ticker-all">Compare all lenders →</SmartLink>
      </div>
      {intro?.length > 0 && <div className="wrap rt-hero-intro" data-testid="rates-hero-intro">{intro.map((p, i) => <p key={i}>{p}</p>)}</div>}
    </section>
  );
}

// City intro under the hero — localized "current mortgage rates in {place}" copy (the legacy pages' ranking intro, in Hello voice).
export const cityRatesIntro = (place) => HUB.city_intro.map((p) => fillPlace(p, place));

// RateWatch+ band for city pages. RateWatch+ runs between approval and closing (see /how-it-works/#rate-hold).
export function RateWatchBand({ place }) {
  const W = HUB.ratewatch;
  return (
    <section className="rt-watch" id="ratewatch" data-testid="ratewatch-band">
      <div className="wrap rt-watch-grid">
        <div>
          <div className="eyebrow">{W.eyebrow}</div>
          <h2 className="display">{W.heading} <em>{W.heading_em}</em></h2>
          <p>{fillPlace(W.text, place)}</p>
          <div className="actions"><SmartLink className="btn light" href={W.how[0]} data-testid="ratewatch-link">{W.how[1]}</SmartLink><SmartLink className="textlink" href={W.after[0]}>{W.after[1]}</SmartLink></div>
        </div>
        <aside className="rt-watch-card" aria-label="RateWatch+ in three steps">
          {W.steps.map(([n, h, p]) => (
            <div className="rt-watch-step" key={n}><span>{n}</span><div><b>{h}</b><small>{p}</small></div></div>
          ))}
        </aside>
      </div>
    </section>
  );
}

// 1b · WHERE ARE YOU AT — onIntent(preset) opens the funnel drawer.
export function IntentBand({ onIntent }) {
  return (
    <section className="rt-intent-band" data-testid="rates-intent-card">
      <div className="wrap rt-intent-grid">
        <div><div className="eyebrow">{HUB.intent.eyebrow}</div><h2 className="rt-intent-k">{HUB.intent.heading}</h2><p className="rt-intent-p">{HUB.intent.text}</p></div>
        <div className="rt-intent-btns">
          <button type="button" className="rt-intent-btn primary" onClick={() => onIntent({ presetLevel1: "buying" })} data-testid="hero-persona-buying"><span>{HUB.intent.buying[0]}</span><em>{HUB.intent.buying[1]}</em></button>
          <button type="button" className="rt-intent-btn" onClick={() => onIntent({ presetLevel1: "renew_refi" })} data-testid="hero-persona-renew-refi"><span>{HUB.intent.renew[0]}</span><em>{HUB.intent.renew[1]}</em></button>
          <div className="rt-intent-micro">{HUB.intent.micro}</div>
        </div>
      </div>
    </section>
  );
}

// 2 · SHORTLIST — the three headline cards with payments on $500k.
export function Shortlist({ rates, ratesLoading, openDrawer, onCompareAll, intro, termBase = "/rates" }) {
  const fixed5 = card(rates, "5yr fixed"), var5 = card(rates, "5yr variable"), fixed3 = card(rates, "3yr fixed");
  const reviews = useReviewsLabel();
  return (
    <section className="rt-shortlist" id="shortlist">
      <div className="wrap">
        <Intro eyebrow={HUB.shortlist.eyebrow} heading={HUB.shortlist.heading} text={intro || HUB.shortlist.text} />
        <div className="rt-cards">
          {[fixed5, var5, fixed3].map((c, i) => {
            const [riskLabel, riskCls] = RISK[c?.risk] || ["Steady", "risk-stable"];
            const pmt = payment(c?.rate);
            return (
              <article className={`rt-card ${i === 0 ? "featured" : ""}`} key={i} data-testid={`featured-rate-card-${i}`}>
                {i === 0 && <div className="rt-card-flag">Most requested</div>}
                <div className="rt-card-top"><span className="rt-card-term">{c?.label || ["5-Year Fixed", "5-Year Variable", "3-Year Fixed"][i]}</span><span className={`rt-risk ${riskCls}`}>{riskLabel}</span></div>
                <div className="rt-card-rate">{ratesLoading ? <SlotRate rate={null} loading /> : fmtRate(c?.rate)}</div>
                <div className="rt-card-lender">{c?.tier_label || "Top Canadian lender"} <span><i className="rt-card-dot">· </i>{c?.channel_label || "Broker channel"}</span></div>
                <dl className="rt-card-pay"><dt>Monthly payment</dt><dd>{money(pmt)}<small>/mo</small></dd></dl>
                <button type="button" className="btn rt-card-btn" onClick={() => openDrawer({ mode: "inquire", inquireContext: c })} data-testid={`inquire-rate-${i}`}>Inquire about this rate</button>
              </article>
            );
          })}
        </div>
        <div className="rt-proof" data-testid="shortlist-proof"><span aria-hidden="true">★★★★★</span> {HUB.shortlist.proof.replace("{reviews}", reviews)}</div>
        <div className="rt-terms" data-testid="term-links"><span>{HUB.shortlist.terms_label}</span>{TERM_LINKS.map(([href, label]) => <SmartLink key={href} href={href.replace("/rates", termBase)}>{label}</SmartLink>)}</div>
        <div className="rt-shortlist-foot"><button type="button" className="textlink" onClick={onCompareAll} data-testid="compare-all-link">{HUB.shortlist.compare}</button><span className="micro">{HUB.shortlist.fine}</span></div>
      </div>
    </section>
  );
}

// Compact forecast strip for city pages: the two verdicts + a link to the full Alberta forecast.
export function ForecastStrip({ insight, outlook, boc, place }) {
  const fixedDir = outlook?.direction || insight?.fixed_direction;
  const varDir = insight?.variable_direction;
  const [fl, fa, fc] = DIR[fixedDir] || ["Reading the market…", "…", "dir-steady"];
  const [vl, va, vc] = DIR[varDir] || ["Reading the market…", "…", "dir-steady"];
  return (
    <section className="rt-strip" data-testid="forecast-strip">
      <div className="wrap">
        <div className="rt-strip-head">
          <Intro eyebrow={HUB.forecast.eyebrow} heading={<>{HUB.strip.heading} <em>{HUB.strip.heading_em}</em></>} text={HUB.strip.text} />
          <img className="rt-strip-art" src="/brand/rates-crystal-ball.png" alt="" loading="lazy" />
        </div>
        <div className="rt-strip-grid">
          <article className={`rt-strip-card ${fc}`} data-testid="strip-fixed"><span className="rt-verdict-k">Fixed rates</span><div className="rt-verdict-v"><i aria-hidden="true">{fa}</i><b>{fl}</b></div>{outlook?.has_range && <small>{outlook.range_label}{outlook.timing_label ? ` · ${outlook.timing_label}` : ""}</small>}{insight?.fixed_take && <p>{insight.fixed_take}</p>}</article>
          <article className={`rt-strip-card ${vc}`} data-testid="strip-variable"><span className="rt-verdict-k">Variable rates</span><div className="rt-verdict-v"><i aria-hidden="true">{va}</i><b>{vl}</b></div>{boc?.next && <small>Bank of Canada meets in {boc.next.days_away} days · {fmtDay(boc.next.date, true)}</small>}{insight?.variable_take && <p>{insight.variable_take}</p>}</article>
        </div>
        <div className="actions"><SmartLink className="btn light" href={HUB.strip.full[0]} data-testid="strip-full-forecast">{HUB.strip.full[1]}</SmartLink><SmartLink className="textlink" href={HUB.strip.desk[0]}>{HUB.strip.desk[1]}</SmartLink></div>
      </div>
    </section>
  );
}

const FVV_ROWS = HUB.fvx.rows;

export function FixedVsVariable({ onCta, place }) {
  const F = HUB.fvx;
  return (
    <section className="rt-fvx" id="fixed-vs-variable" data-testid="fixed-vs-variable">
      <div className="wrap">
        <div className="rt-fvx-head">
          <Intro eyebrow={F.eyebrow} heading={<>{place ? fillPlace(F.heading_city, place) : F.heading} <em>{F.heading_em}</em></>} text={F.text.replace("{who}", place ? fillPlace(F.who_city, place) : F.who_default)} />
          <img className="rt-fvx-art" src="/brand/fixed-vs-variable-ring.png" alt="Two percent-sign characters with boxing gloves facing off in a boxing ring" loading="lazy" data-testid="fvx-art" />
        </div>
        <div className="rt-fvx-table" role="table">
          <div className="rt-fvx-row rt-fvx-th" role="row"><div />{F.corners.map(([c, s], i) => <div className={`corner corner-${i === 0 ? "fixed" : "variable"}`} key={c}>{c}<small>{s}</small></div>)}</div>
          {FVV_ROWS.map(([k, f, v]) => (
            <div className="rt-fvx-row" role="row" key={k}><div className="rt-fvx-k">{k}</div><div>{f}</div><div>{v}</div></div>
          ))}
        </div>
        <div className="rt-fvx-foot">
          <p><b>{F.take_strong}</b> {F.take}</p>
          <div className="actions"><button type="button" className="btn" onClick={onCta} data-testid="fvx-cta">{F.cta}</button><SmartLink className="textlink" href={F.guide[0]}>{F.guide[1]}</SmartLink></div>
        </div>
      </div>
    </section>
  );
}

const shorten = (t, n) => (t.length <= n ? t : t.slice(0, t.lastIndexOf(" ", n)) + "…");
export const fmtDay = (iso, long) => new Date(iso).toLocaleDateString("en-CA", { month: long ? "long" : "short", day: "numeric" });

const LEAN = {
  up: "and the next move is more likely up than down.",
  down: "and the next move is more likely a cut than a hike.",
  balanced: "and the next move is a genuine coin flip.",
};
const NEXT_CALL = { hold: "Hold", hike: "Hike", cut: "Cut" };

// Variable card: two horizons — the next Bank of Canada announcement, then the 6–9 month path. Copy grounded in the analyst brief when one is fresh.
function VariablePanel({ insight, boc, prime }) {
  const dir = insight?.variable_direction;
  const [label, arrow, cls] = DIR[dir] || ["Reading the market…", "…", "dir-steady"];
  const call = NEXT_CALL[insight?.variable_next_call] || "Hold";
  const bocRate = boc?.latest?.rate != null ? `${Number(boc.latest.rate).toFixed(2)}%` : boc?.latest?.decision?.match(/to\s+(\d+\.\d+%)/)?.[1];
  const path = insight?.variable_path || [];
  return (
    <article className={`rt-fc ${cls}`} data-testid="forecast-variable">
      <span className="rt-verdict-k">Variable rates</span>
      <div className="rt-verdict-v"><i aria-hidden="true">{arrow}</i><b>{label}</b></div>
      <em className="rt-fc-h" data-testid="variable-lean">{dir ? `${dir === "steady" ? "Nothing moves before" : "All eyes on"} ${boc?.next?.date ? fmtDay(boc.next.date) : "the next announcement"}—${LEAN[insight?.variable_lean] || LEAN.balanced}` : ""}</em>

      {boc?.latest?.date && <div className="rt-fc-latest" data-testid="variable-latest-decision"><span>Latest decision</span><b>{(() => { const d = boc.latest.decision?.replace(/^The Bank of Canada\s+/i, "").replace(/\.$/, "") || "—"; return d.charAt(0).toUpperCase() + d.slice(1); })()}</b><small>· {fmtDay(boc.latest.date, true)}</small></div>}
      <span className="rt-fc-block">Short term · the next announcement</span>
      <div className="rt-fc-stats" data-testid="variable-short-term">
        <div><span>Bank of Canada meets</span><b>{boc?.next?.days_away != null ? `${boc.next.days_away} days` : "—"}</b><small>{boc?.next?.date ? fmtDay(boc.next.date, true) : ""}</small></div>
        <div><span>Our expected call</span><b>{dir ? call : "—"}</b><small>{bocRate ? `Policy rate ${bocRate} · Prime ${prime != null ? fmtRate(prime) : "—"}` : ""}</small></div>
        <div><span>What could change it</span><b className="rt-fc-stat-text">{insight?.variable_tripwire || "—"}</b></div>
      </div>
      {insight?.variable_why?.length > 0 && <div className="rt-why"><span className="rt-why-k">Why we're calling a {call.toLowerCase()}</span><ol>{insight.variable_why.map((w, i) => <li key={i}>{w}</li>)}</ol></div>}

      {path.length > 0 && (
        <>
          <span className="rt-fc-block">Long term · where Prime is likely heading{insight?.forecast_source === "broker" && <> · <b data-testid="variable-house-view">our team's call{insight.house_view?.as_of ? `, set ${insight.house_view.as_of}` : ""}</b></>}</span>
          <ol className="rt-path" data-testid="variable-path">
            {path.map((p, i) => <li key={i}><span>{p.when}</span><b>{p.call}</b></li>)}
          </ol>
          {insight?.variable_path_why && <p className="rt-fc-path-why" data-testid="variable-path-why">{insight.variable_path_why}</p>}
        </>
      )}

      {insight?.variable_take && <p className="rt-fc-take">{insight.variable_take}</p>}
      {insight?.analyst_used && <small className="rt-fc-src" data-testid="variable-analyst-source">Analyst data{insight.analyst_date ? ` · ${fmtDay(insight.analyst_date)}` : ""} · Bank of Canada guidance{boc?.latest?.date ? ` · ${fmtDay(boc.latest.date)}` : ""}</small>}
    </article>
  );
}
export const DIR = {
  up: ["Heading up", "↑", "dir-up"], down: ["Easing", "↓", "dir-down"], steady: ["Holding steady", "→", "dir-steady"],
};
// Decision Desk: today's numbers → the break-even → three Prime scenarios → our call + who it's for.
const WHO = HUB.desk.who;
function DecisionDesk({ desk, onCta }) {
  const cmp = desk?.comparison, call = desk?.signal, be = desk?.breakeven, rows = desk?.scenarios || [];
  const lean = call?.lean === "LEAN_FIXED" ? "fixed" : call?.lean === "LEAN_VARIABLE" ? "variable" : null;
  const who = WHO[lean || "variable"];
  const maxHikes = Math.max(be?.hikes_until_fixed_wins ?? 0, be?.forecast_hikes ?? 0, be?.stress_hikes ?? 0, 3);
  return (
    <div className="rt-desk-grid">
      <article className="rt-fvv" data-testid="decision-desk-card">
        <span className="eyebrow">Today's numbers · honestly</span>
        <div className="rt-fvv-tiles">
          {[cmp?.fixed, cmp?.variable].map((t, i) => <div className={`rt-fvv-tile ${cmp?.cheaper_today === (i === 0 ? "fixed" : "variable") ? "win" : ""}`} key={i}><span>{t?.label || (i === 0 ? "5-Year Fixed" : "5-Year Variable")}</span><b>{fmtRate(t?.rate)}</b><em>{t?.monthly_payment ? `${money(t.monthly_payment)}/mo` : "—/mo"}</em></div>)}
        </div>
        <div className="micro">On a {money(cmp?.reference_loan || 500000)} mortgage · {cmp?.amort_years || 25}-year amortization{cmp?.monthly_savings ? ` · ${cmp.cheaper_today} saves ${money(cmp.monthly_savings)} a month today` : ""}{desk?.rates_estimated ? " · estimated from today's bond yield and Prime" : ""}</div>

        {be?.hikes_until_fixed_wins != null && (
          <div className="rt-be" data-testid="desk-breakeven">
            <span className="rt-why-k">The one number that settles it</span>
            <h4>Variable wins unless Prime rises more than <em>{be.threshold_pct.toFixed(2)}%</em> over the term.</h4>
            <p>That's {be.hikes_until_fixed_wins} quarter-point hike{be.hikes_until_fixed_wins === 1 ? "" : "s"}{be.capped ? " or more" : ""}. Our forecast has {be.forecast_hikes > 0 ? `${be.forecast_hikes}${be.forecast_label ? ` by ${be.forecast_label.toLowerCase()}` : ""}` : "none on the board"}.</p>
            <div className="rt-be-bar" aria-hidden="true">
              {Array.from({ length: maxHikes }, (_, i) => <span key={i} className={i < (be.forecast_hikes || 0) ? "on" : ""} />)}
              <i className="rt-be-mark fc" style={{ left: `${((be.forecast_hikes || 0) / maxHikes) * 100}%` }}><small>Our forecast</small></i>
              <i className="rt-be-mark fx" style={{ left: `${(Math.min(be.hikes_until_fixed_wins, maxHikes) / maxHikes) * 100}%` }}><small>Fixed starts winning</small></i>
            </div>
            <div className="rt-be-axis"><span>0 hikes</span><span>{maxHikes} hikes (+{(maxHikes * 0.25).toFixed(2)}%)</span></div>
          </div>
        )}

        {rows.length > 0 && (
          <div className="rt-scen" data-testid="desk-scenarios">
            <span className="rt-why-k">Three futures, one table</span>
            <table>
              <thead><tr><th>If this happens…</th><th>Fixed pays</th><th>Variable pays</th><th>Winner</th></tr></thead>
              <tbody>{rows.map((r) => <tr key={r.name} className={r.name === "Our base case" ? "base" : ""}><td><b>{r.name}</b><small>{r.prime_path}</small></td><td>{money(r.fixed_interest)}</td><td>{money(r.variable_interest)}</td><td><b className={r.winner}>{r.winner === "variable" ? "Variable" : "Fixed"}</b><small>by {money(r.margin)}</small></td></tr>)}</tbody>
            </table>
            <div className="micro">Total interest over a 5-year term on {money(cmp?.reference_loan || 500000)}. Variable payments re-set at each move.</div>
          </div>
        )}

        <div className={`rt-call lean-${(call?.lean || "neutral").toLowerCase()}`} data-testid="desk-call">
          <span>Our call</span>
          <b>{HUB.desk.calls[lean || "neutral"]}</b>
          <p>{call?.headline || "We're missing a data point right now—check back shortly."}</p>
          <div className="rt-who">{who.map(([h, items]) => <div key={h}><span>{h}</span><ul>{items.map((t) => <li key={t}>{t}</li>)}</ul></div>)}</div>
        </div>
        <div className="rt-desk-cta">
          <button type="button" className="btn" onClick={onCta} data-testid="desk-cta">See my personalized call</button>
          <span className="micro">Which one is actually yours? Two minutes, no credit check.</span>
        </div>
      </article>
    </div>
  );
}

// "Why your rate isn't your uncle's rate" — how a personal rate is actually set.
export const FACTORS = HUB.factors.items;
export function RateFactors({ onCta, place }) {
  const F = HUB.factors;
  return (
    <section className="rt-factors" id="how-rates-work" data-testid="rate-factors">
      <div className="wrap">
        <Intro eyebrow={place ? fillPlace(F.eyebrow_city, place) : F.eyebrow} heading={<>{F.heading} <em>{F.heading_em}</em></>} text={F.text.replace("{where}", place ? fillPlace(F.where_city, place) : "")} />
        <div className="rt-factor-grid">
          {FACTORS.map(([k, h, body, chip, link], i) => (
            <article className="rt-factor" key={k} data-testid={`rate-factor-${i}`}>
              <div className="rt-factor-top"><span className="rt-factor-k">{k}</span>{chip === "See the Decision Desk" ? <a className="rt-factor-chip" href="#decision-desk">{chip}</a> : <span className="rt-factor-chip">{chip}</span>}</div>
              <h3>{h}</h3>
              <p>{body}</p>
              {link && <SmartLink className="textlink rt-factor-link" href={link[0]}>{link[1]}</SmartLink>}
            </article>
          ))}
        </div>
        <div className="rt-factor-cta">
          <div><h3>{F.cta_heading}</h3><p>{F.cta_text}</p></div>
          <button type="button" className="btn" onClick={onCta} data-testid="factors-cta">{F.cta}</button>
        </div>
      </div>
    </section>
  );
}

const FAQ_LINKS = HUB.faq.links;
function linkify(text) {
  const rx = new RegExp(`(${FAQ_LINKS.map(([t]) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`);
  let used = new Set();
  return text.split(rx).map((part, i) => {
    const hit = FAQ_LINKS.find(([t]) => t === part);
    if (!hit || used.has(part)) return part;
    used.add(part);
    return hit[1].startsWith("#") ? <a key={i} href={hit[1]}>{part}</a> : <SmartLink key={i} href={hit[1]}>{part}</SmartLink>;
  });
}
function RatesFaq({ seo, place }) {
  const faqs = seo?.faqs || [];
  if (!faqs.length) return null;
  return (
    <>
      {seo?.schema && <Helmet><script type="application/ld+json">{JSON.stringify(seo.schema)}</script></Helmet>}
      <FaqSection items={faqs.map((f) => [f.q, linkify(f.a)])} eyebrow={fillPlace(HUB.faq.eyebrow, place)} heading={HUB.faq.heading} visible={8} />
    </>
  );
}

// Alberta housing market this month — AREA snapshot rows (province + major cities), the local depth competitors can't match.
const ALBERTA_BLOCKS = HUB.market.blocks;
const pct = (n) => (n == null ? "—" : `${n > 0 ? "+" : ""}${n}%`);
// Months of supply → who has leverage. Mirrors the footnote thresholds (~4 buyers, ~2.5 sellers).
const leverage = (m) => (m == null ? null : m >= 4 ? ["buyers", "Buyer's market"] : m <= 2.5 ? ["sellers", "Seller's market"] : ["balanced", "Balanced"]);

// What it takes to buy at each city's average — down payment, payment, income; toggles minimum vs 20% down like the city pages.
function BuyingTable({ rows }) {
  const [mode, setMode] = useState("minimum");
  if (!rows.length) return null;
  const b0 = rows[0].buying;
  return (
    <div className="rt-buy" data-testid="buying-table">
      <div className="rt-buy-bar">
        <div><span className="rt-why-k">{HUB.market.buy_k}</span><p>{HUB.market.buy_text}</p></div>
        <div className="snap-toggle" role="group" aria-label="Down payment scenario" data-testid="rates-down-toggle">
          {[["minimum", "Minimum down"], ["conventional", "20% down"]].map(([k, l]) => (
            <button type="button" key={k} className={mode === k ? "on" : ""} aria-pressed={mode === k} onClick={() => setMode(k)} data-testid={`rates-down-toggle-${k}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="rt-market-table rt-buy-table" role="table" aria-label="Down payment, monthly payment and income to qualify by city">
        <div className="rt-market-head" role="row"><span>City</span><span>Average price</span><span>Down payment</span><span>Monthly payment</span><span>Income to qualify</span></div>
        {rows.map((r) => { const sc = r.buying.scenarios.find((x) => x.key === mode); return (
          <div className="rt-market-row rt-market-row-labelled" role="row" key={r.slug} data-testid={`buy-row-${r.slug}`}>
            <span data-l="City"><SmartLink href={`/${r.slug}/`}>{r.name}</SmartLink></span>
            <span data-l="Average price"><b>{money(r.avg_price)}</b></span>
            <span data-l="Down payment">{money(sc.down)} <small>{sc.down_pct}%{sc.cmhc_premium ? ` · CMHC ${money(sc.cmhc_premium)}` : ""}</small></span>
            <span data-l="Monthly payment" className="hl"><b>{money(sc.monthly_payment)}</b> <small>/mo</small></span>
            <span data-l="Income to qualify">~{money(sc.income_needed)} <small>/yr</small></span>
          </div>); })}
      </div>
      <div className="micro">Payments at {b0.rate.toFixed(2)}% over {b0.amort_years} years{mode === "minimum" ? ", CMHC premium added to the mortgage" : ", no default insurance"}; income at the {b0.qualifying_rate.toFixed(2)}% stress-test rate with property tax and heat included.{b0.is_live ? "" : " Rate is an estimate until today's lender sync lands."}</div>
    </div>
  );
}
function AlbertaMarket({ rows, place }) {
  if (!rows?.length) return null;
  const period = rows[0].period_label;
  return (
    <section className="rt-market" id="alberta-market" data-testid="alberta-market">
      <div className="wrap">
        <Intro eyebrow={fillPlace(HUB.market.eyebrow, place).replace("{period}", period)} heading={<>{HUB.market.heading}<em>{HUB.market.heading_em}</em></>} text={HUB.market.text} />
        <div className="rt-market-table" role="table" aria-label={`${place} housing market snapshot`}>
          <div className="rt-market-head" role="row"><span>Market</span><span>Average price</span><span>Price vs last year</span><span>Homes sold</span><span>Months of supply · leverage</span></div>
          {rows.map((r) => (
            <div className={`rt-market-row rt-market-row-labelled ${r.slug === "alberta" ? "prov" : ""}`} role="row" key={r.slug} data-testid={`market-row-${r.slug}`}>
              <span data-l="Market">{r.slug === "alberta" ? <b>{r.name}</b> : <SmartLink href={`/${r.slug}/`}>{r.name}</SmartLink>}</span>
              <span data-l="Average price"><b>{money(r.avg_price)}</b></span>
              <span data-l="Price vs last year" className={r.avg_price_yoy > 0 ? "up" : r.avg_price_yoy < 0 ? "down" : ""}>{pct(r.avg_price_yoy)}</span>
              <span data-l="Homes sold">{r.sales?.toLocaleString("en-CA")} <small>{pct(r.sales_yoy)} vs last yr</small></span>
              <span data-l="Months of supply">{r.months_of_supply}{leverage(r.months_of_supply) && <em className={`rt-lev is-${leverage(r.months_of_supply)[0]}`}>{leverage(r.months_of_supply)[1]}</em>}</span>
            </div>
          ))}
        </div>
        <div className="micro">Source: {rows[0].source_label || "AREA"} · {period}. {HUB.market.note}</div>
        <BuyingTable rows={rows.filter((r) => r.slug !== "alberta" && r.buying)} />
        <div className="rt-market-blocks">
          {ALBERTA_BLOCKS.map(([h, body, href, label]) => <article key={h}><h3>{h}</h3><p>{body}</p><SmartLink className="textlink" href={href}>{label}</SmartLink></article>)}
        </div>
      </div>
    </section>
  );
}

// "Go deeper" — curated Vault guides + calculators, titles pulled live so edits in the Vault flow through.
const CALCS = HUB.deeper.calcs;
export function GoDeeper({ reading }) {
  const guides = reading?.guides || [];
  const D = HUB.deeper;
  if (!guides.length) return null;
  return (
    <section className="rt-deeper" data-testid="go-deeper">
      <div className="wrap">
        <Intro eyebrow={D.eyebrow} heading={<>{D.heading} <em>{D.heading_em}</em></>} text={D.text} />
        <div className="rt-deeper-grid">
          {guides.map((g) => (
            <SmartLink className="rt-deeper-card" href={`/strategy-vault/${g.slug}/`} key={g.slug} data-testid={`deeper-guide-${g.slug}`}>
              <span className="rt-deeper-k">{D.guide_k}{g.reading_minutes ? ` · ${g.reading_minutes} min` : ""}</span>
              <h3>{g.title}</h3>
              {g.summary && <p>{g.summary}</p>}
              <span className="textlink">{D.guide_cta}</span>
            </SmartLink>
          ))}
          {CALCS.map(([href, name, blurb]) => (
            <SmartLink className="rt-deeper-card calc" href={href} key={href} data-testid={`deeper-calc-${name.toLowerCase().replace(/\s+/g, "-")}`}>
              <span className="rt-deeper-k">{D.calc_k}</span>
              <h3>{name}</h3>
              <p>{blurb}</p>
              <span className="textlink">{D.calc_cta}</span>
            </SmartLink>
          ))}
        </div>
      </div>
    </section>
  );
}

const TERM_LINKS = HUB.shortlist.term_links;

// Sticky rate-hold bar: appears after the forecast scrolls past, only while fixed rates are heading up.
export function StickyHoldBar({ outlook, place }) {
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem("hm_holdbar_dismissed") === "1");
  useEffect(() => {
    const onScroll = () => {
      const fc = document.querySelector("[data-testid='rate-intelligence'], [data-testid='forecast-strip'], [data-testid='term-forecast'], [data-testid='goal-rate-table']");
      if (!fc) return;
      setShow(fc.getBoundingClientRect().bottom < 0);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  if (dismissed || !show || outlook?.direction !== "up") return null;
  const rng = outlook?.has_range && outlook.range_label ? ` · ${outlook.range_label} expected` : "";
  return (
    <div className="rt-holdbar" role="status" data-testid="sticky-hold-bar">
      <span className="rt-holdbar-dot" aria-hidden="true" />
      <b>Fixed rates heading up{rng}</b>
      <span className="rt-holdbar-sub">A free 120-day hold locks today's {place ? `${place} ` : ""}rate. Rates fall? You get the lower one. And all the other lenders try to chase you.</span>
      <CalendlyLink className="btn" testId="sticky-hold-cta">Lock in a rate hold</CalendlyLink>
      <button type="button" className="rt-holdbar-x" aria-label="Dismiss" onClick={() => { sessionStorage.setItem("hm_holdbar_dismissed", "1"); setDismissed(true); }} data-testid="sticky-hold-dismiss">×</button>
    </div>
  );
}

// Text-me-my-rates: phone-only capture, fired by exit intent (desktop mouse-out / mobile back-scroll), once per visitor.
export function TextMyRates({ source = "rates_exit_intent", place }) {
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState("");
  const [state, setState] = useState("idle");
  useEffect(() => {
    if (localStorage.getItem("hm_textme_seen") === "1") return undefined;
    let lastY = window.scrollY, armed = false;
    const arm = setTimeout(() => { armed = true; }, 12000);
    const fire = () => { if (!armed) return; localStorage.setItem("hm_textme_seen", "1"); setOpen(true); cleanup(); };
    const onLeave = (e) => { if (e.clientY <= 0) fire(); };
    const onScroll = () => { const y = window.scrollY; if (y > 900 && lastY - y > 260) fire(); lastY = y; };
    const cleanup = () => { document.removeEventListener("mouseleave", onLeave); window.removeEventListener("scroll", onScroll); clearTimeout(arm); };
    document.addEventListener("mouseleave", onLeave);
    window.addEventListener("scroll", onScroll, { passive: true });
    return cleanup;
  }, []);
  if (!open) return null;
  const submit = async (e) => {
    e.preventDefault();
    setState("sending");
    try {
      await publicApi.post("/text-my-rates", { phone, source });
      setState("sent");
    } catch (err) {
      setState(err?.response?.status === 422 ? "bad" : "error");
    }
  };
  return (
    <div className="rt-textme" role="dialog" aria-label="Text me today's rates" data-testid="text-my-rates">
      <button type="button" className="rt-holdbar-x" aria-label="Close" onClick={() => setOpen(false)} data-testid="text-my-rates-close">×</button>
      <img src="/brand/stackup-trophy.png" alt="" className="rt-textme-art" />
      {state === "sent" ? (
        <div><b>Here's our number. It's in your texts.</b><p>Today's three rates, your free hold link, and a number you can actually call. Maybe. No call from us unless you ask for one.</p></div>
      ) : (
        <form onSubmit={submit}>
          <b>Leaving without your {place ? `${place} ` : ""}rate?</b>
          <p>We'll text you today's three lowest {place ? `${place} ` : ""}rates and a free hold link. No call, no credit check, no drip.</p>
          <div className="rt-textme-row">
            <input type="tel" inputMode="tel" autoComplete="tel" placeholder="Your mobile number" value={phone} onChange={(e) => setPhone(e.target.value)} required data-testid="text-my-rates-phone" />
            <button type="submit" className="btn" disabled={state === "sending"} data-testid="text-my-rates-submit">{state === "sending" ? "Sending…" : "Text me"}</button>
          </div>
          {state === "bad" && <small className="rt-textme-err">That doesn't look like a Canadian mobile number.</small>}
          {state === "error" && <small className="rt-textme-err">Something hiccupped. Try again in a moment.</small>}
          <small className="rt-textme-fine">One text. Reply STOP anytime.</small>
        </form>
      )}
    </div>
  );
}

// 12-month chart: our lowest 5-yr fixed vs the 5-yr bond yield. Pure SVG, no lib.
function RateHistoryChart() {
  const [d, setD] = useState(null);
  useEffect(() => { publicApi.get("/rate-history-series").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d?.has_rates || !d?.has_bonds) return null;
  const pts = d.points.filter((p) => p.lowest_5y_fixed != null || p.bond_yield != null);
  const W = 1000, H = 320, PL = 72, PR = 46, PT = 18, PB = 34;
  const vals = pts.flatMap((p) => [p.lowest_5y_fixed, p.bond_yield]).filter((v) => v != null);
  const lo = Math.floor(Math.min(...vals) * 4) / 4 - 0.25, hi = Math.ceil(Math.max(...vals) * 4) / 4 + 0.25;
  const x = (i) => PL + (i / Math.max(1, pts.length - 1)) * (W - PL - PR);
  const y = (v) => PT + (1 - (v - lo) / (hi - lo)) * (H - PT - PB);
  const path = (key) => pts.map((p, i) => (p[key] == null ? null : `${i === 0 || pts[i - 1][key] == null ? "M" : "L"}${x(i).toFixed(1)},${y(p[key]).toFixed(1)}`)).filter(Boolean).join(" ");
  const ticks = []; for (let v = lo + 0.25; v < hi; v += 0.5) ticks.push(v);
  const last = pts[pts.length - 1], first = pts[0];
  const monthLabels = pts.map((p, i) => [i, p.date]).filter(([i, dt], k, arr) => i === 0 || dt.slice(0, 7) !== arr[k - 1][1].slice(0, 7)).filter((_, k) => k % 2 === 0);
  const delta = last?.lowest_5y_fixed != null && first?.lowest_5y_fixed != null ? last.lowest_5y_fixed - first.lowest_5y_fixed : null;
  return (
    <div className="rt-chart" data-testid="rate-history-chart">
      <div className="rt-chart-head">
        <div><span className="rt-why-k">{HUB.chart.k.replace("{months}", d.months)}</span><h3>{HUB.chart.heading} <em>{HUB.chart.heading_em}</em>{HUB.chart.heading_tail}</h3></div>
        <div className="rt-chart-legend"><span className="l-rate">Lowest 5-yr fixed{last?.lowest_5y_fixed != null ? ` · ${last.lowest_5y_fixed.toFixed(2)}%` : ""}</span><span className="l-bond">5-yr bond yield{last?.bond_yield != null ? ` · ${last.bond_yield.toFixed(2)}%` : ""}</span></div>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="rt-chart-svg" role="img" aria-label="Lowest 5-year fixed mortgage rate versus 5-year Government of Canada bond yield over the last 12 months">
        {ticks.map((v) => <g key={v}><line x1={PL} x2={W - PR} y1={y(v)} y2={y(v)} className="grid" /><text x={PL - 8} y={y(v) + 4} textAnchor="end" className="tick" dangerouslySetInnerHTML={{ __html: `${v.toFixed(2)}%` }} /></g>)}
        {monthLabels.map(([i, dt]) => <text key={dt} x={x(i)} y={H - 10} textAnchor="middle" className="tick" dangerouslySetInnerHTML={{ __html: new Date(dt + "T12:00:00").toLocaleDateString("en-CA", { month: "short" }) }} />)}
        <path d={path("bond_yield")} className="line-bond" />
        <path d={path("lowest_5y_fixed")} className="line-rate-shadow" />
        <path d={path("lowest_5y_fixed")} className="line-rate" />
        {last?.lowest_5y_fixed != null && <circle cx={x(pts.length - 1)} cy={y(last.lowest_5y_fixed)} r="7" className="dot" />}
      </svg>
      <p className="micro">{delta != null ? `The lowest 5-year fixed is ${delta > 0 ? "up" : "down"} ${Math.abs(delta).toFixed(2)}% over the period. ` : ""}{HUB.chart.note}</p>
    </div>
  );
}

// "How we know" — the rate-intelligence explainer under the forecast cards, fed by live counts from /market-signal.
function RateIntel({ signal, insight, weekly }) {
  const n = (v) => (v != null ? Number(v).toLocaleString("en-CA") : null);
  const I = HUB.intel;
  const steps = I.steps.map(([k, v], i) => [k, i === 1 && signal?.events_tracked ? I.connect_live.replace("{n}", n(signal.events_tracked)) : v]);
  return (
    <div className="rt-intel" data-testid="rate-intel">
      <div className="rt-intel-head">
        <span className="eyebrow">{I.eyebrow}</span>
        <h3>{I.heading}<em>{I.heading_em}</em></h3>
        <p>{I.text}</p>
      </div>
      <ol className="rt-intel-steps">{steps.map(([k, v]) => <li key={k}><b>{k}</b><span>{v}</span></li>)}</ol>
      <div className="rt-take-by">Hello Mortgage Insights{insight?.updated_at && <> · updated {fmtDay(insight.updated_at)}</>}{insight?.analyst_used && insight?.analyst_date ? ` · Analyst data · ${fmtDay(insight.analyst_date)}` : ""}{signal?.events_tracked ? ` · ${n(signal.events_tracked)}+ lender rate moves tracked` : ""}{weekly?.href && <> · <SmartLink href={weekly.href} className="rt-weekly-link" data-testid="weekly-report-link">{I.weekly_link}</SmartLink></>}</div>
    </div>
  );
}


const FIXED_SUB = {
  up: (m) => `Bond yields jumped ${m}% this week—and lenders don't sit on that for long.`,
  down: (m) => `Bond yields dropped ${m}% this week—lenders are slower on the way down, but they get there.`,
  steady: () => "Barely a ripple in bond yields this week—nothing here to push lenders either way.",
};

// Fixed card: the bond move → expected lender repricing (from our own rate history) → what it costs you → rate hold nudge.
function FixedPanel({ insight, signal, outlook, onHold }) {
  const dir = outlook?.direction || insight?.fixed_direction;
  const [label, arrow, cls] = DIR[dir] || ["Reading the market…", "…", "dir-steady"];
  const d5 = signal?.d5_change_bps ?? outlook?.bond_d5_bps;
  const moveTxt = d5 != null ? Math.abs(d5 / 100).toFixed(2) : null;
  const n = outlook?.nudge;
  const path = insight?.fixed_path || [];
  return (
    <article className={`rt-fc ${cls}`} data-testid="forecast-fixed">
      <span className="rt-verdict-k">Fixed rates</span>
      <div className="rt-verdict-v"><i aria-hidden="true">{arrow}</i><b>{label}</b></div>
      <em className="rt-fc-h" data-testid="fixed-sub">{dir && moveTxt != null ? FIXED_SUB[dir](moveTxt) : ""}</em>

      <span className="rt-fc-block">Short term · the next few days</span>
      <div className="rt-fc-stats" data-testid="fixed-short-term">
        <div><span>5-year bond yield</span><b>{signal?.bond_yield != null ? `${signal.bond_yield.toFixed(2)}%` : "—"}</b><small>{d5 != null ? `${d5 > 0 ? "up" : d5 < 0 ? "down" : "flat"} ${moveTxt}% this week` : ""}</small></div>
        <div><span>Where fixed rates are headed</span>
          <b className={outlook?.has_range ? "rt-fc-stat-range" : "rt-fc-stat-text"} data-testid="fixed-range">{outlook?.has_range ? outlook.range_label : dir === "steady" ? "None expected" : dir ? (dir === "up" ? "Up" : "Down") : "—"}</b>
          <small>{outlook?.has_range ? "based on how lenders have reacted before" : dir === "steady" ? "Rates should sit tight" : ""}</small></div>
        <div><span>How soon</span><b className="rt-fc-stat-text">{outlook?.has_range && outlook.timing_label ? outlook.timing_label : dir === "steady" ? "Nothing on the radar" : insight?.horizon || "—"}</b>
          <small>{outlook?.lenders_moved_72h > 0 ? `${outlook.lenders_moved_72h} lender${outlook.lenders_moved_72h === 1 ? "" : "s"} have already moved` : ""}</small></div>
      </div>
      {outlook?.has_range && <small className="rt-fc-src" data-testid="fixed-sample">We've watched {outlook.events_total.toLocaleString("en-CA")}+ lender rate changes land after moves like this one</small>}

      {insight?.fixed_why?.length > 0 && <div className="rt-why"><span className="rt-why-k">Why we're calling it {dir === "up" ? "up" : dir === "down" ? "down" : "steady"}</span><ol>{insight.fixed_why.map((w, i) => <li key={i}>{w}</li>)}</ol></div>}

      {path.length > 0 && (
        <>
          <span className="rt-fc-block">Long term · where fixed rates are heading</span>
          <ol className="rt-path" data-testid="fixed-path">{path.map((p, i) => <li key={i}><span>{p.when}</span><b>{p.call}</b></li>)}</ol>
          {insight?.fixed_path_why && <p className="rt-fc-path-why">{insight.fixed_path_why}</p>}
        </>
      )}

      {dir === "up" && n && (
        <div className="rt-nudge" data-testid="fixed-nudge">
          <span className="rt-why-k">What +{n.move_pct.toFixed(2)}% actually costs you</span>
          <div className="rt-nudge-nums">
            <div><b>{money(n.buying_power_delta)}</b><span>less house you can buy</span></div>
            <div><b>{money(n.payment_delta_mo)}</b><span>more a month, every month</span></div>
            <div><b>{money(n.interest_delta_5y)}</b><span>more interest over a 5-year term</span></div>
          </div>
          <p>A rate hold is free, takes minutes, and keeps today's rate safe for up to 120 days. Rates drop before you close? You get the lower one. Easiest yes of your week.</p>
          <div className="rt-nudge-cta">
            <button type="button" className="btn" onClick={onHold} data-testid="rate-hold-cta">Lock in a rate hold</button>
            <small>On a $500,000 mortgage, 25-year amortization{n.rate_is_estimate ? ", estimated from today's bond yield" : ""}.</small>
          </div>
        </div>
      )}

      {insight?.fixed_take && <p className="rt-fc-take">{insight.fixed_take}</p>}
      <small className="rt-fc-src">Hello Mortgage rate data{insight?.analyst_used && insight?.analyst_date ? ` · Analyst data · ${fmtDay(insight.analyst_date)}` : ""}</small>
    </article>
  );
}

// A percent sign with flapping wings — the rates mascot. CSS-only animation (see .rt-flock in site.css).
export function WingedPercent({ variant, scale }) {
  // Outlined % with feathered wings, pink offset "misregistered" fill behind (hello-bubble style). Wings flap via CSS on the <g>.
  const oval = (cx, cy, rx, ry) => `M${cx - rx} ${cy} a${rx} ${ry} 0 1 0 ${2 * rx} 0 a${rx} ${ry} 0 1 0 ${-2 * rx} 0 Z`;
  const ring = (cx, cy) => `${oval(cx, cy, 24, 29)} ${oval(cx, cy, 11, 15)}`;
  // Pink pass = solid offset shapes (the "3D" misprint); navy pass = outlined rings with a see-through hole so the pink shows.
  const Body = ({ fill, stroke, dx = 0, dy = 0 }) => (
    <g transform={`translate(${dx},${dy})`}>
      <line x1="184" y1="40" x2="116" y2="180" stroke={stroke || fill} strokeWidth={stroke ? 26 : 28} strokeLinecap="round" />
      {stroke && <line x1="184" y1="40" x2="116" y2="180" stroke="#fff" strokeWidth="14" strokeLinecap="round" />}
      {stroke
        ? <><path d={ring(116, 68)} fill="#fff" fillRule="evenodd" stroke={stroke} strokeWidth="6" /><path d={ring(184, 152)} fill="#fff" fillRule="evenodd" stroke={stroke} strokeWidth="6" /></>
        : <><path d={oval(116, 68, 24, 29)} fill={fill} /><path d={oval(184, 152, 24, 29)} fill={fill} /></>}
    </g>
  );
  return (
    <div className={`wp wp-${variant}`} style={{ "--s": scale }}>
      <svg viewBox="0 0 300 220" aria-hidden="true">
        <g className="wp-wing-l"><g transform="translate(4,36)"><image href="/brand/wing-left.png" x="6" y="-8" width="90" height="108" /></g></g>
        <g className="wp-wing-r"><g transform="translate(296,40) scale(-1,1)"><image href="/brand/wing-left.png" x="6" y="-8" width="90" height="108" /></g></g>
        <Body fill="var(--pink2)" dx={9} dy={7} />
        <Body fill="#fff" stroke="var(--navy)" />
      </svg>
    </div>
  );
}

const STEPS = HUB.steps.items;

export function RatesSteps({ onCta }) {
  return (
    <section className="how rates-steps" id="rates-how" data-testid="rates-how">
      <div className="wrap">
        <Intro eyebrow={HUB.steps.eyebrow} heading={HUB.steps.heading} text={HUB.steps.text} />
        <div className="steps">
          {STEPS.map(([img, h, em, p], i) => <article className="step" key={h}><img className="step-art" src={img} alt="" loading="lazy" width="210" height="210" data-testid={`rates-step-art-0${i + 1}`} /><h3>{h}<em>{em}</em></h3><p>{p}</p></article>)}
        </div>
        <div className="actions rates-steps-cta"><button type="button" className="btn" onClick={onCta} data-testid="how-it-works-cta">{HUB.steps.cta}</button></div>
      </div>
    </section>
  );
}

const STACK_ROWS = HUB.trust.rows;
const STACK_COLS = HUB.trust.cols;
export function RatesTrust({ onCta }) {
  return (
    <section className="rates-trust" data-testid="stack-up">
      <div className="wrap">
        <Intro eyebrow={HUB.trust.eyebrow} heading={<>{HUB.trust.heading} <em>{HUB.trust.heading_em}</em></>} text={HUB.trust.text} />
        <div className="rt-stack" role="table" aria-label="How Hello Mortgage compares to big banks and rate comparison sites">
          <div className="rt-stack-head" role="row">
            <span />
            {STACK_COLS.map(([h, sub], i) => <div className={`rt-stack-col ${i === 0 ? "us" : ""}`} key={h} role="columnheader">{i === 0 && <img src="/brand/stackup-trophy.png" alt="" className="rt-stack-trophy" />}<b>{h}</b><small>{sub}</small></div>)}
          </div>
          {STACK_ROWS.map(([label, marks], r) => (
            <div className="rt-stack-row" role="row" key={label} data-testid={`stack-row-${r}`}>
              <span className="rt-stack-label">{label}</span>
              {marks.map((on, i) => <div className={`rt-stack-cell ${i === 0 ? "us" : ""}`} key={i} role="cell">{on ? (i === 0 ? <Star /> : <Check />) : <i className="rt-stack-dash" aria-label="No" />}</div>)}
            </div>
          ))}
          <div className="rt-stack-row rt-stack-cta" role="row"><span /><div className="rt-stack-cell us"><CalendlyLink className="btn" testId="trust-cta">{HUB.trust.cta}</CalendlyLink></div><span /><span /></div>
        </div>
      </div>
    </section>
  );
}
const Star = () => <svg className="rt-star" viewBox="0 0 24 24" aria-label="Yes"><path d="M12 2.5l2.9 6.1 6.6.8-4.9 4.6 1.3 6.6L12 17.4l-5.9 3.2 1.3-6.6L2.5 9.4l6.6-.8z" /></svg>;
const Check = () => <svg className="rt-check" viewBox="0 0 24 24" aria-label="Yes"><path d="M4 12.5l5 5L20 6.5" fill="none" /></svg>;
